/**
 * The lapsed-account paywall. Replaces the old full-page paywall
 * (`components/layout/InactiveAccountDialog.tsx`, now only used by a dev preview).
 *
 * What changed from the old paywall, and why:
 *
 * 1. It floats over the LIVE portal. AppShell no longer early-returns for a lapsed account, so the
 *    nav and the user's real page stay behind the scrim (inert via TopNavigation `locked`).
 *    A lapsed user's first read should be "my account is paused", not "my account is gone".
 * 2. It says what is still there. Screens / files / playlists counts, fetched with
 *    count-only queries, because the fear at this moment is "did I lose my work".
 * 3. Two actions: Manage Account (root Owner) or Logout (everyone else, and white-label), and
 *    Subscribe for an expired trial or Update Payment for a lapsed paid account — plus the old
 *    paywall's winback claim when the user arrived from an OptiReach link. The old Delete
 *    Account (a real DELETE_COMPANY, one confirm away) is gone — deleting the account is not a
 *    thing to offer next to the button that saves it.
 * 4. It mounts NO billing dialogs. The old paywall had to (AppShell returned before
 *    <BillingDialogs /> mounted); here the shell renders, so the global mount owns the wizard
 *    and mounting our own would stack two focus traps over each other.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { signOut, auth } from '@/lib/firebase';
import { Button, cn, Dialog, DialogContent, DialogDescription, DialogTitle } from '@scio3/ui';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useBillingData } from '@/features/billing/hooks/useBillingData';
import { isOptiSignsDomain } from '@/components/layout/InactiveAccountDialog';
import { useBillingDialogStore } from '@/features/billing/stores/billing-dialog-store';
import { BILLING_ARTWORK } from '@/lib/brand-assets';
import { LAPSED_FALLBACK_ACCOUNT_PATH, LAPSED_MANAGE_ACCOUNT_PATH } from './lapsed-settings-paths';
import { LapsedPlanFeatures } from './LapsedPlanFeatures';
import { LapsedProPlusFeatures } from './LapsedProPlusFeatures';
import { useWinbackClaim } from './useWinbackClaim';

type PaymentData = { isTrial?: boolean; trialEndDate?: string };

export function InactiveAccountDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openBillingDialog = useBillingDialogStore((s) => s.openDialog);
  const activeBillingDialog = useBillingDialogStore((s) => s.activeDialog);
  const me = useAuthStore((s) => s.me);
  /** Initial focus target — see onOpenAutoFocus on DialogContent. */
  const choosePlanRef = useRef<HTMLButtonElement>(null);

  // White-label accounts get no OptiSigns-branded upgrade path (mirrors the old paywall's
  // `showUpdate` gate) — for them Logout is the only action.
  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;

  // Why the account is locked, on the same flag the old paywall used: an expired trial gets the
  // "trial has ended" pitch, anything else is a paid account whose payment lapsed and gets the
  // "needs attention" copy with an Update Payment action.
  const payment = me?.payment as PaymentData | undefined;
  const isTrial = payment?.isTrial === true;
  // A trial can be locked BEFORE it ends (#2215: a declined card flipped `payment.isActive` with
  // days left). "Trial has ended" would be false there, so it gets its own small dialog. Anh's
  // rule: end date still in the future → "Your account needs attention"; no date, or an
  // unparseable one, falls back to "trial has ended".
  const trialEndMs = payment?.trialEndDate ? Date.parse(payment.trialEndDate) : NaN;
  const isTrialStillRunning = isTrial && Number.isFinite(trialEndMs) && trialEndMs > Date.now();
  const { maxScreens, planFeature } = useBillingData();

  // Same gates as the old paywall's Update and Claim buttons: the upgrade path is
  // OptiSigns-branded, so it hides on white-label AND on a non-OptiSigns domain; the winback
  // claim shows only for a stored OptiReach capture, and never on white-label.
  const { isRootAdmin, isRootOwner } = usePermissions();
  // Paying is admin-only on the server (stripeBuildElement is BILLING_ROLES), so a member who
  // isn't a root admin would reach a payment form that never loads (#2290). They are told to
  // ask an admin instead of being offered Subscribe / Update Payment.
  const canPay = isRootAdmin;
  const showUpgrade = isOptiSignsDomain() && !isWhiteLabel && canPay;
  const { hasWinbackOffer, isClaiming, claim } = useWinbackClaim();
  const showClaimTrial = hasWinbackOffer && !isWhiteLabel;

  // Subscription Plan is admin-only, and white-label can hide it outright. Sending anyone
  // else there would bounce off SettingsLayout's access check straight back into the
  // paywalled portal, so non-admins land on Personal Profile instead.
  const canSeeSubscriptionPage = isRootAdmin && !me?.brandingData?.hideSubscriptionPages;
  const manageAccountPath = canSeeSubscriptionPage
    ? LAPSED_MANAGE_ACCOUNT_PATH
    : LAPSED_FALLBACK_ACCOUNT_PATH;

  // Manage Account is the root Owner's (the old paywall's Delete Account gate). Anyone else, and
  // every white-label account, gets Logout in its place, as on the old paywall.
  const showManageAccount = isRootOwner && !isWhiteLabel;

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      // Even if signOut fails, navigate away — auth state clears on next load.
    }
    navigate('/signIn');
  };

  // Step aside while the wizard (or any billing dialog) is open, exactly as the old paywall
  // did on `childDialogOpen` — AppShell keeps us mounted, so we reappear on close if the
  // account is still lapsed.
  const childDialogOpen = activeBillingDialog !== null;

  const handleChoosePlan = () => {
    // Same entry the old paywall used (#1413): trial/zero-quota accounts need the
    // wizard's screen-count step, not a card-only edit, or they re-subscribe to 0 screens.
    openBillingDialog('subscriptionWizard', { fromInactive: true });
  };

  // The old paywall's Update routing (#1413): a paid account that still has a subscription
  // (maxScreens > 0) only needs a new card; a cancelled one (no quota left) has to re-subscribe.
  const handleUpdatePayment = () => {
    if (maxScreens === 0) {
      handleChoosePlan();
      return;
    }
    openBillingDialog('billingInformation');
  };

  // Trial still running but locked (#2215): the same small text-only popup as white-label, not
  // the trial pitch. The artwork and "What you had on Pro Plus" read as "your trial is over",
  // which is exactly what is NOT true here. Admins can still subscribe; a member is told to ask an
  // admin (#2290); white-label keeps Logout only, as on its other lapsed popups.
  if (isTrialStillRunning) {
    const canSubscribeHere = showUpgrade;
    return (
      <Dialog open={!childDialogOpen}>
        <DialogContent
          data-testid="inactive-account-dialog"
          data-variant="trial-needs-attention"
          hideCloseButton
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => {
            // Focus Subscribe when it is there (the action this popup exists for); otherwise no
            // auto-focus ring on the lone Logout.
            e.preventDefault();
            choosePlanRef.current?.focus();
          }}
          className="max-w-lg gap-6"
          overlayClassName="bg-black/40"
        >
          <div className="space-y-2">
            <DialogTitle className="text-lg font-semibold">
              {t('Your account needs attention')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('Your content is safe.')}{' '}
              {isWhiteLabel
                ? t('Choose a plan to continue using your account.')
                : canPay
                  ? t('Please choose a plan to continue.')
                  : t('Please contact your account admin to upgrade your plan.')}
            </DialogDescription>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="inactive-logout-btn" onClick={handleLogout}>
              {t('Logout')}
            </Button>
            {canSubscribeHere && (
              <Button
                ref={choosePlanRef}
                data-testid="inactive-choose-plan-btn"
                onClick={handleChoosePlan}
              >
                {t('Subscribe')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // White-label: a small text-only popup, like the old paywall's Attention alert. The artwork and
  // feature lists are OptiSigns marketing, and Logout is the only action, so the two-column shell
  // would be a big empty card around one button.
  if (isWhiteLabel) {
    return (
      <Dialog open={!childDialogOpen}>
        <DialogContent
          data-testid="inactive-account-dialog"
          data-variant={isTrial ? 'trial-ended' : 'needs-attention'}
          hideCloseButton
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          // No auto-focus ring on the lone button: it isn't the action we want clicked, just
          // the only one there is.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="max-w-lg gap-6"
          overlayClassName="bg-black/40"
        >
          <div className="space-y-2">
            <DialogTitle className="text-lg font-semibold">
              {isTrial
                ? t('Your Pro Plus trial has ended')
                : t('Your subscription needs attention')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('Your content is safe.')}{' '}
              {isTrial
                ? t('Choose a plan to continue using your account.')
                : t('Please update your payment information to continue.')}
            </DialogDescription>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" data-testid="inactive-logout-btn" onClick={handleLogout}>
              {t('Logout')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    // Dialog rather than AlertDialog: AlertDialogContent hardcodes its overlay, and this
    // popup needs a lighter scrim so the portal behind stays legible. Same non-dismissible
    // recipe as AppShell's onboarding survey modal.
    <Dialog open={!childDialogOpen}>
      <DialogContent
        data-testid="inactive-account-dialog"
        data-variant={isTrial ? 'trial-ended' : 'needs-attention'}
        hideCloseButton
        // Non-dismissible: no close button, Escape and outside-click both blocked. Seeing
        // the portal behind is reassurance, not an invitation to browse.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // Radix focuses the first focusable child on open. Send that initial focus to the
        // primary action instead: it is the one thing this popup wants clicked, so it is the
        // right place for a keyboard user to land anyway.
        // (It also used to land on the "Your content is safe" tooltip trigger, and Radix Tooltip
        // opens on focus, so the tooltip popped by itself on every mount. That trigger is gone
        // now, but the redirect is still the behaviour we want.)
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          choosePlanRef.current?.focus();
        }}
        // Two-column shell. Padding lives on the left column so the right panel can run
        // full-bleed to the card edge.
        // w-[calc(100vw-4rem)] replaces the base's `w-full` (tailwind-merge keeps the last
        // class in the `w` group), so the card keeps a 2rem gutter on each side instead of
        // running edge to edge once the viewport drops below the max width.
        // 60rem (960px), not `max-w-5xl` (64rem/1024px): the grid below is an even
        // `grid-cols-2`, so trimming the shell takes the same 32px off the copy column and the
        // artwork panel rather than squeezing one. An arbitrary value because the next step on
        // the scale, `max-w-4xl` (56rem), is a 128px drop — a different dialog, not a trim.
        className="w-[calc(100vw-4rem)] max-w-[60rem] gap-0 overflow-hidden p-0 sm:max-w-[60rem]"
        // Lighter than the default bg-black/60 so the portal underneath reads through.
        overlayClassName="bg-black/40"
      >
        {/* Capped at 88vh with a single minmax(0,1fr) row so the left column can own the
            overflow: without the explicit track the row sizes to its content and the cell's
            overflow-y-auto never engages, clipping the CTAs on a short laptop viewport. */}
        {/* Two columns only at `lg`, NOT `sm`. The dialog is `max-w-[60rem]` (960px) and
            `w-[calc(100vw-4rem)]`, so it reaches full width at a 1024px viewport — exactly the `lg`
            breakpoint. Splitting at `sm` (640px) gave each column ~288px, which squeezed the
            artwork panel into a portrait sliver and made `object-cover` crop the collage down its
            centre. Tying the split to `lg` means the artwork appears only when the dialog is at its
            full 960px and there is actually room for it; narrower than that, it drops out and the
            copy gets the whole card. */}
        <div className="grid max-h-[88vh] grid-cols-1 grid-rows-[minmax(0,1fr)] lg:grid-cols-2">
          {/* min-h sets the card's height: grid cells stretch to the tallest row, so the
              media panel follows this column. Content sits top, CTAs bottom (mt-auto), and
              the leftover space becomes breathing room between them. */}
          {/* One inset for every block in here, copy and actions alike. Per-block padding made
              the buttons sit at a different gridline from the text above them, and a reduced
              RIGHT padding made the stacked full-width buttons asymmetric (2rem left, 1rem
              right). The gap to the artwork is closed on the artwork instead — see its margin
              below — so nothing here has to be traded against it.
              `pb-6` is the one deliberate asymmetry: the button row is the last thing in the
              column and carries its own height, so a full 2rem underneath read as dead space
              below the actions rather than as breathing room. */}
          <div className="flex min-h-[34rem] flex-col gap-6 overflow-y-auto p-8 pb-6">
            <div className="space-y-2">
              <DialogTitle className="text-2xl font-semibold">
                {isTrial
                  ? t('Your Pro Plus trial has ended')
                  : t('Your subscription needs attention')}
              </DialogTitle>
              {/* The reassurance is one line now; the proof (what is actually still there)
                  moved into the tooltip so the counts do not compete with the Pro Plus
                  pitch below for the same glance. */}
              <DialogDescription className="text-sm text-muted-foreground">
                {t('Your content is safe.')}{' '}
                {/* What to DO, on the same line as the reassurance. The title says the trial
                    ended and the next line said only that nothing was lost, which left the
                    dialog stating a problem and a non-consequence with no instruction between
                    them and the plan cards below.

                    Brand name gated: a white-label account has no OptiSigns to continue using,
                    and naming it would leak the platform into a customer's own portal. Same
                    flag as the Pro Plus pitch and the Subscribe button below. Two whole keys
                    rather than interpolating the product name, per the i18n rules. */}
                {!canPay
                  ? t('Please contact your account admin to upgrade your plan.')
                  : !isTrial
                    ? t('Please update your payment information to continue.')
                    : isWhiteLabel
                      ? t('Choose a plan to continue using your account.')
                      : t('Choose a plan to continue using your OptiSigns account.')}
              </DialogDescription>
            </div>

            {/* The value half: what the trial actually gave them. Sits below the "nothing was
                lost" reassurance on purpose — safety first, then the reason to come back.
                White-label accounts have no OptiSigns upgrade path, so the pitch is dead
                weight for them (same gate as the Subscribe button). */}
            {/* Trial only: a paid account may never have been on Pro Plus, so "what you had on
                Pro Plus" would be wrong for it. */}
            {isTrial && !isWhiteLabel && <LapsedProPlusFeatures />}
            {/* Paid lapse: the plan they're paying for, with its wizard plan-card bullets. */}
            {!isTrial && !isWhiteLabel && <LapsedPlanFeatures planFeature={planFeature} />}

            {/* pt-10 guarantees separation from the copy even when the counts list is long
                enough to eat the mt-auto slack.

                Stacked below `lg`, side by side above it. The threshold is arithmetic, not taste:
                this column is `min(100vw - 4rem, 64rem) / 2` wide (the card splits at `sm` and
                caps at `max-w-5xl`) less its own `p-8`, so usable width is ~288px at `md` and
                ~416px at `lg`. The pair needs ~372px in English and more in longer languages, so
                anything under `lg` pushed Subscribe out of the column — which is what produced the
                horizontal scrollbar, since `overflow-y-auto` forces overflow-x to auto too.

                No inset of its own — it takes the column's, so the buttons line up with the
                copy above them in both layouts. */}
            <div className="mt-auto pt-10">
              <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-3">
                {/* Outline secondary, matching the primary's `lg` height so the two still line up as
                    a row. The border is what gives the lesser action a defined target next to a
                    solid Subscribe, rather than a label floating in white space.
                    `hover:border-transparent` because outline's hover fills the box while keeping
                    its stroke, which leaves a hard edge around the fill — dropping it lets the
                    hover read as one soft solid shape (same treatment as the Standard plan card's
                    CTA). */}
                {showManageAccount ? (
                  <Button
                    variant="outline"
                    size="lg"
                    data-testid="inactive-manage-account-btn"
                    onClick={() => navigate(manageAccountPath)}
                    // Full width only while stacked; back to intrinsic width once the row fits.
                    //
                    // NO negative margin, deliberately. There used to be a `lg:-ml-6` pulling the
                    // button left so its LABEL sat on the copy's gridline — but the box is the shape
                    // you actually see (now literally, with a border on it), and the pull pushed that
                    // box a full 1.5rem outside the column. The box is what gets aligned here; the
                    // label sitting `px-6` inside it is the normal way a button reads.
                    className={cn(
                      'w-full hover:border-transparent lg:w-auto',
                      showClaimTrial ? 'px-4' : 'px-6',
                    )}
                  >
                    {t('Manage Account')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="lg"
                    data-testid="inactive-logout-btn"
                    onClick={handleLogout}
                    className="w-full px-6 hover:border-transparent lg:w-auto"
                  >
                    {t('Logout')}
                  </Button>
                )}
                {/* A winback offer takes the primary slot in place of Subscribe / Update
                    Payment: the account can still update its card from Manage Account, and two
                    competing primaries next to each other read as a choice the user has to
                    weigh. */}
                {showClaimTrial ? (
                  <Button
                    ref={choosePlanRef}
                    size="lg"
                    data-testid="inactive-claim-trial-btn"
                    onClick={claim}
                    disabled={isClaiming}
                    // px-4 on both buttons in this pair: "Claim your 30-day free trial" plus
                    // Manage Account only just fit the ~416px column in English. flex-wrap on the
                    // row drops the claim to its own line in a longer language instead of
                    // pushing it under the artwork.
                    className="order-first w-full px-4 lg:order-none lg:w-auto"
                  >
                    {isClaiming ? t('Claiming...') : t('Claim your 30-day free trial')}
                  </Button>
                ) : (
                  showUpgrade && (
                    <Button
                      ref={choosePlanRef}
                      size="lg"
                      data-testid={
                        isTrial ? 'inactive-choose-plan-btn' : 'inactive-update-payment-btn'
                      }
                      onClick={isTrial ? handleChoosePlan : handleUpdatePayment}
                      // Wider than the secondary: this is the one action the popup exists to get
                      // clicked, so it reads as the heavier of the two.
                      //
                      // `lg:min-w-48` (192px), not just padding: white-on-green reads optically
                      // lighter than near-black-on-white at the same size and weight, and "Subscribe" is 9
                      // characters against "Manage Account"'s 14, so padding alone left the primary
                      // looking the smaller of the two. A floor also keeps it the wider button in
                      // languages where the labels' relative lengths flip. Scoped to `lg` because the
                      // stacked layout is `w-full` on both, where a min-width could only overflow.
                      //
                      // order-first: stacked, the action this popup exists for belongs on top. At `lg`
                      // it returns to its DOM position, to the right of the secondary.
                      className="order-first w-full px-12 lg:order-none lg:w-auto lg:min-w-48"
                    >
                      {isTrial ? t('Subscribe') : t('Update Payment')}
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Artwork panel — an inset card, not a full-bleed half. The margin is what pulls it
              off the dialog's own edges so the rounded corner reads on all four sides; the
              divider border is gone with it, since a floating panel does not need a seam.
              No LEFT margin though: that edge faces the copy, which already holds 2rem of its
              own padding, and stacking a second 1rem on top left a 3rem trench between the text
              and the image. The other three sides keep theirs — they face the dialog edge, where
              there is nothing else supplying the inset.
              `rounded-xl` (12px) is the largest radius the design system allows.
              The gradient is the designer's three stops (see --color-media-gradient-* in
              globals.css), angled per the Figma source: mint anchored at the BOTTOM-LEFT running
              to near-white at the top-right, hence `to-tr`. The mid stop sits at 15%, which is
              what keeps the mint concentrated in that corner instead of fading evenly across the
              whole panel. The inset shadow is what stops the panel reading as a flat coloured
              rectangle: it seats the artwork inside a well rather than on a swatch. */}
          <div
            aria-hidden="true"
            className="hidden min-h-[34rem] overflow-hidden rounded-xl bg-gradient-to-tr from-media-gradient-from via-media-gradient-via via-15% to-media-gradient-to shadow-[inset_0_2px_12px_rgba(0,0,0,0.10)] lg:mb-4 lg:mr-4 lg:mt-4 lg:block"
            data-testid="inactive-account-media"
          >
            {/* Transparent-background collage, bottom-anchored and horizontally CENTRED.
                `object-cover` therefore trims whichever axis is tight, and the crop is shared:
                as the dialog narrows it takes an even bite off BOTH sides rather than eating the
                right edge alone, which used to walk the cake composition off the panel while
                dead mint sat on the left. Vertically it stays bottom-anchored — the Bake Sale /
                cake grouping is the part worth seeing, and the screens table at the top tolerates
                being cut.
                Height is a plain `h-full`, no overflow trick: the export carries ~3.5% of its own
                height as transparent bottom margin (measured, and proportional so it survives a
                re-export at any resolution), which at this panel's scale lands around 20px and
                reads as the padding under the artwork. This used to be
                `h-[calc(100%+1rem)]` to CLIP that margin away and sit the artwork flush; the
                clip is gone because flush was too tight. That calc is the knob if the gap ever
                needs tuning — plus opens it up, minus closes it. No `scale-*`: the image and the
                panel are within a few percent of the same aspect ratio, so scaling pushes the
                composition off all four sides instead of trimming one.

                `object-cover`, deliberately — a tight crop is preferred over letterboxing here, so
                the panel always reads as a full-bleed image. The crop tightens as the panel gets
                more portrait, which is why the two-column split is gated at `lg` rather than `sm`:
                below the dialog's full width the artwork is dropped entirely instead of being
                squeezed. Do NOT "fix" a tight crop with `object-contain` — that letterboxes the
                artwork against the gradient and looks worse than the crop it replaces. If the crop
                is ever too tight at full width, cap the panel's height instead. */}
            <img
              src={BILLING_ARTWORK.trialExpired}
              alt=""
              className="h-full w-full object-cover object-bottom"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
